/**
 * Punches in and out, per week or per month.
 *
 * The field-quality team's request of 28 Sep 2026: "the number of incoming tickets per
 * week/month, and the number of tickets closed by the contractors over the same period", to
 * follow how the contractors work through them. Decided with Jérémy the same day: THREE
 * series — in, done by the contractor, validated by Unifiber. The request is about the
 * contractors' work, which is their Done; but only Unifiber's Validated closes an item
 * (17 Sep 2026), and the gap between the two is Réginald's validation queue — a median of 7
 * days after the Done, measured on the 263 validated punches.
 *
 * Pure: no clock and no timezone. Every instant arrives as a zoned ISO string
 * (`zonedIsoString`, served by the API in the snapshot's own zone), so the day an event
 * happened on is its first ten characters, and nothing here needs to know which zone it is.
 *
 * ⚠ **A punch counts at the moment its CURRENT status was set** — the board keeps no earlier
 * one. So the past moves: a Done that Réginald sends back (Redo) leaves the "done" bar of its
 * week, and a Redo later validated without a new Done comes back to the week of its first
 * Done. The bars describe the board as it stood that morning, and the screen says so.
 * Measured over the first six mornings of snapshots: nothing left Done or Validated.
 */
import { isContractorDone, STATUS_UNIFIBER } from "./quality-audits.js";
import { dateOnly, isoDateUtc, isoWeekKey } from "./time.js";
/** In, done by the contractor, validated by Unifiber — in that order, on every chart. */
export const FLOW_EVENTS = ["created", "done", "validated"];
/** The day part of a zoned ISO instant: the day in the zone it was written in. */
function dayOf(instant) {
    if (instant === null)
        return null;
    const match = /^(\d{4}-\d{2}-\d{2})T/.exec(instant);
    return match ? match[1] : null;
}
/**
 * Has this event happened to the punch at all, dated or not?
 *
 * Every punch came in. "Done" is `isContractorDone` — a Done Unifiber has not sent back — and
 * "validated" is the Unifiber status, the one that closes an item.
 */
function eventApplies(row, event) {
    switch (event) {
        case "created":
            return true;
        case "done":
            return isContractorDone(row);
        case "validated":
            return row.statusUnifiber === STATUS_UNIFIBER.VALIDATED;
    }
}
/** Which instant dates the event: creation, or the moment the status was last set. */
function instantOf(row, event) {
    switch (event) {
        case "created":
            return row.createdAt;
        case "done":
            return row.statusContractorChangedAt;
        case "validated":
            return row.statusUnifiberChangedAt;
    }
}
/**
 * The day an event happened to a punch, `YYYY-MM-DD` — null when it has not happened, or
 * cannot be dated. The one definition the chart, the period filter, the headline tile and the
 * export all read, so a bar, the rows it filters to and the file they export agree.
 */
export function eventDayOf(row, event) {
    return eventApplies(row, event) ? dayOf(instantOf(row, event)) : null;
}
/** `2026-W39` or `2026-09`: the period a day belongs to. Weeks are ISO — Monday to Sunday. */
export function periodKeyOf(day, unit) {
    return unit === "week" ? isoWeekKey(dateOnly(day)) : day.slice(0, 7);
}
/** The week number of a `2026-W39` key — what a label shows as S39 or W39. */
export function weekNumberOf(key) {
    return Number(key.slice(6));
}
function addDays(day, n) {
    return isoDateUtc(new Date(dateOnly(day).getTime() + n * 86_400_000));
}
/** The first and last day of a period, `YYYY-MM-DD`. */
export function periodRange(key, unit) {
    const year = Number(key.slice(0, 4));
    if (unit === "month") {
        const month = Number(key.slice(5, 7));
        // Day 0 of the next month is the last day of this one.
        return { first: `${key}-01`, last: isoDateUtc(new Date(Date.UTC(year, month, 0))) };
    }
    // ISO week 1 is the week that holds 4 January; every week starts on a Monday.
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const monday = new Date(jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86_400_000);
    const first = addDays(isoDateUtc(monday), (weekNumberOf(key) - 1) * 7);
    return { first, last: addDays(first, 6) };
}
/**
 * The periods a chart shows: `count` of them, oldest first, ending with the one that holds
 * the EVE of the snapshot's day.
 *
 * ⚠ The eve, because the snapshot is taken at 05:00 — it holds the board up to the end of the
 * day before. Ending on the snapshot's own day would put an empty bar last every Monday, the
 * day of the contractors' weekly meeting, and every 1st of the month. The last period is
 * `partial` only when the eve is not its final day: Monday's snapshot shows a whole week.
 *
 * ⚠ Stepped by DATES, never by week numbers: 2026 has 53 ISO weeks, and counting down from
 * W01 to W52 of the year before would skip one without anybody noticing.
 */
export function flowWindow(runDate, unit, count = 12) {
    const eve = addDays(runDate, -1);
    const periods = [];
    let inside = eve;
    for (let i = 0; i < count; i++) {
        const key = periodKeyOf(inside, unit);
        const { first, last } = periodRange(key, unit);
        periods.push({ key, first, last, partial: eve < last });
        inside = addDays(first, -1);
    }
    return periods.reverse();
}
/**
 * How many punches came in, were done and were validated, period by period.
 *
 * Counts whatever rows it is given: the page hands it `flowBase` — every filter but "open
 * only" and the period itself. An event outside the window is simply not drawn; the window is
 * the question being asked, not a loss.
 */
export function flowSeries(rows, unit, runDate, count = 12) {
    const periods = flowWindow(runDate, unit, count).map((p) => ({ ...p, created: 0, done: 0, validated: 0 }));
    const byKey = new Map(periods.map((p) => [p.key, p]));
    const undated = { created: 0, done: 0, validated: 0 };
    for (const row of rows) {
        for (const event of FLOW_EVENTS) {
            if (!eventApplies(row, event))
                continue;
            const day = dayOf(instantOf(row, event));
            if (day === null) {
                undated[event]++;
                continue;
            }
            const bucket = byKey.get(periodKeyOf(day, unit));
            if (bucket)
                bucket[event]++;
        }
    }
    return { periods, undated };
}
/**
 * The last ISO week a snapshot holds in full: the week before the one of its day.
 *
 * (It dated "last week's balance", the fourth tile of 28 Sep 2026 — in minus validated. Jérémy
 * judged a balance not relevant the next day: it moves with the auditors' intake and with
 * Réginald's validations, not with the contractors' work. The tile became the door to the
 * contractors' table, whose trend this week anchors — see quality-work.ts.)
 */
export function lastCompleteWeek(runDate) {
    const current = periodRange(periodKeyOf(runDate, "week"), "week");
    const key = periodKeyOf(addDays(current.first, -1), "week");
    return { key, ...periodRange(key, "week") };
}
//# sourceMappingURL=quality-flow.js.map